import { isCmmdAuthFailureError } from "@cmmd-forge/shared/cmmdAuthFailure";
import { type SavedEnvironmentRecord } from "./catalog";
import * as savedCloud from "./savedCloudEnvironmentLifecycle";
import { invalidatePullRequestsForEnvironment } from "./environmentConnectionHandlers";

const CMMD_RUNTIME_BOOTSTRAP_RETRY_DELAYS_MS = [
  1_000, 2_000, 4_000, 8_000, 12_000, 16_000, 20_000,
] as const;
export const CMMD_RUNTIME_BOOTSTRAP_MAX_ATTEMPTS = 8;
const CMMD_RUNTIME_METADATA_TIMEOUT_MS = 90_000;
const TRANSIENT_BOOTSTRAP_MESSAGE_PARTS = [
  "all fibers interrupted without error",
  "timed out while waiting for forge cloud runtime metadata",
  "timed out while waiting for",
  "to stream its shell snapshot",
  "forge runtime route not found",
  "fly-replay",
  "could not find a good candidate",
  "instance refused connection",
] as const;
const TRANSIENT_BOOTSTRAP_EXACT_MESSAGE_PARTS = [
  "Unknown socket error",
  "Failed to fetch remote auth endpoint",
  "Forge runtime replay target is unavailable",
  "Remote auth request failed (502)",
  "Remote auth request failed (503)",
  "Remote auth request failed (504)",
  "Unable to connect to the Forge server WebSocket",
] as const;

function getErrorStatus(error: unknown): number | null {
  if (typeof error !== "object" || error === null) return null;
  const status = (error as { readonly status?: unknown }).status;
  return typeof status === "number" && Number.isInteger(status) ? status : null;
}

export function isTransientCmmdRuntimeBootstrapError(error: unknown): boolean {
  if (isCmmdAuthFailureError(error)) return false;

  const status = getErrorStatus(error);
  if (status === 502 || status === 503 || status === 504) return true;

  // FINDING-14: a startup shell-snapshot failure surfaces as an
  // OrchestrationGetSnapshotError (e.g. the VM is still warming up). Match it by
  // its stable `_tag` rather than its message so the initial-bootstrap path
  // keeps its existing transient-retry behavior when subscribeShell.onError
  // rejects the bootstrap gate with this error.
  const tag = (error as { readonly _tag?: unknown })._tag;
  if (tag === "OrchestrationGetSnapshotError") return true;

  const message = error instanceof Error ? error.message : String(error);
  const normalizedMessage = message.toLowerCase();
  // removeConnection interrupts the previous stream. Effect can report that
  // interruption without an inner failure, so the retry budget still applies.
  return (
    TRANSIENT_BOOTSTRAP_MESSAGE_PARTS.some((part) => normalizedMessage.includes(part)) ||
    TRANSIENT_BOOTSTRAP_EXACT_MESSAGE_PARTS.some((part) => message.includes(part))
  );
}

// Test surface: exposes internal helpers whose behavior is load-bearing but
// which are not part of the module's public API (mirrors apps/server ws.ts
// `wsTestSurface`). FINDING-14 added the OrchestrationGetSnapshotError `_tag`
// branch above; its classification gates the bootstrap retry loop, so it is
// covered directly here.
export const runtimeServiceTestSurface = {
  isTransientCmmdRuntimeBootstrapError,
  invalidatePullRequestsForEnvironment,
};

export function cmmdRuntimeBootstrapRetryDelayMs(attemptIndex: number): number {
  if (import.meta.env.MODE === "test") return 0;
  return (
    CMMD_RUNTIME_BOOTSTRAP_RETRY_DELAYS_MS[attemptIndex] ??
    CMMD_RUNTIME_BOOTSTRAP_RETRY_DELAYS_MS[CMMD_RUNTIME_BOOTSTRAP_RETRY_DELAYS_MS.length - 1]!
  );
}

function resolveCmmdRuntimeMetadataTimeoutMs(): number {
  return import.meta.env.MODE === "test" ? 5 : CMMD_RUNTIME_METADATA_TIMEOUT_MS;
}

export function withCmmdRuntimeMetadataTimeout<T>(
  record: SavedEnvironmentRecord,
  operation: Promise<T>,
): Promise<T> {
  if (!savedCloud.isCmmdCloudEnvironment(record)) {
    return operation;
  }

  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = globalThis.setTimeout(() => {
      reject(
        new Error(
          `Timed out while waiting for Forge Cloud runtime metadata for ${record.environmentId}. The VM may still be starting.`,
        ),
      );
    }, resolveCmmdRuntimeMetadataTimeoutMs());
  });

  return Promise.race([operation, timeout]).finally(() => {
    if (timeoutId !== null) {
      globalThis.clearTimeout(timeoutId);
    }
  });
}
